import { AIEditButton } from './actions/ai-edit/AIEditButton';
import { AIEditContent } from './actions/ai-edit/AIEditContent';
import { AIEditPanel } from './actions/ai-edit/AIEditPanel';
import { AIEditReactionButtons } from './actions/ai-edit/AIEditReactionButtons';
import { AIEditProvider } from './actions/ai-edit/ai-edit-context';
import { QueryEditorContainer } from './QueryEditorContainer';
import { QueryEditorFeedback } from './QueryEditorFeedback';
import { QueryEditorHelpText } from './QueryEditorHelpText';
import { QueryEditorInput } from './QueryEditorInput';
import { QueryEditorInputContainer } from './QueryEditorInputContainer';
import { QueryEditorModeContent } from './QueryEditorModeContent';
import { QueryEditorModeToggle } from './QueryEditorModeToggle';
import { QueryEditorOptionsCollapse } from './QueryEditorOptionsCollapse';
import { QueryEditorRoot } from './QueryEditorRoot';
import { QueryEditorToolbar } from './QueryEditorToolbar';
import { QueryEditorToolbarActionItem } from './QueryEditorToolbarActionItem';
import { QueryEditorToolbarActions } from './QueryEditorToolbarActions';
import { QueryEditorToolbarDivider } from './QueryEditorToolbarDivider';

/**
 * Compound component primitives for a single query editor.
 *
 * Structural primitives define the editor's compositional skeleton (Root, Container, Toolbar, ToolbarActions,
 * ToolbarActionItem, ToolbarDivider, ModeToggle, ModeContent, InputContainer, Input, Feedback, OptionsCollapse,
 * HelpText).
 *
 * AI Edit components provide the "Edit with AI" action and are self-gating — they read context to decide
 * whether to render, so consumers can compose them unconditionally.
 *
 * For the common case, `AIEditContent` pre-composes the full AI edit layout:
 *
 * ```
 * <QueryEditor.Root value={query} onChange={setQuery} onRun={handleRun}>
 *   <QueryEditor.AIEditProvider onSubmit={generateSuggestion}>
 *     <QueryEditor.AIEditContent
 *       extensions={promqlExtensions}
 *       endAdornment={<ClearAction />}
 *       thinkingMessages={['Generating PromQL...']}
 *     />
 *   </QueryEditor.AIEditProvider>
 * </QueryEditor.Root>
 * ```
 *
 * For finer control, compose the individual primitives directly:
 *
 * ```
 * <QueryEditor.Root value={query} onChange={setQuery} onRun={handleRun}>
 *   <QueryEditor.AIEditProvider onSubmit={generateSuggestion}>
 *     <QueryEditor.Container>
 *       <QueryEditor.InputContainer>
 *         <QueryEditor.Input
 *           extensions={promqlExtensions}
 *           endAdornment={<QueryEditor.AIEditButton />}
 *         />
 *       </QueryEditor.InputContainer>
 *       <QueryEditor.AIEditReactionButtons />
 *       <QueryEditor.AIEditPanel />
 *     </QueryEditor.Container>
 *   </QueryEditor.AIEditProvider>
 * </QueryEditor.Root>
 * ```
 */
export const QueryEditor = {
  Root: QueryEditorRoot,
  Input: QueryEditorInput,
  Container: QueryEditorContainer,
  Toolbar: QueryEditorToolbar,
  ToolbarActions: QueryEditorToolbarActions,
  ToolbarActionItem: QueryEditorToolbarActionItem,
  ToolbarDivider: QueryEditorToolbarDivider,
  ModeToggle: QueryEditorModeToggle,
  ModeContent: QueryEditorModeContent,
  InputContainer: QueryEditorInputContainer,
  Feedback: QueryEditorFeedback,
  OptionsCollapse: QueryEditorOptionsCollapse,
  HelpText: QueryEditorHelpText,
  AIEditProvider,
  AIEditContent,
  AIEditButton,
  AIEditPanel,
  AIEditReactionButtons,
};
